import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = {
  class: "sr-only",
  "aria-live": "polite"
}
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = {
  class: "sr-only",
  "aria-live": "polite"
}
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = { class: "sr-only" }
const _hoisted_8 = ["min", "step", "aria-label"]
const _hoisted_9 = ["aria-label"]

import {
  computed,
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  useAttrs,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { BigNumber } from 'bignumber.js';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import WCMenuDropdownModern from '@/components/WCMenuDropdownModern/WCMenuDropdownModern.vue';
import useCartControls from '@/composables/useCartControls';
import useQuantityDisplay from '@/modules/orders/composables/useQuantityDisplay';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';
import { ENTRY_METHOD, EntryMethod } from '@/models/entities/cart/entry-method';
import { useStore } from 'vuex';
import ModalService from '@/modules/modals/services/modal.service';
import ExitScanAndGo from '@/views/ScanProduct/ExitScanAndGo/ExitScanAndGo.vue';

interface PriceEmbeddedLineItem {
  embeddedPrice?: number;
  originalPrice?: number;
  embeddedDiscount?: number;
  expiration?: string;
  embeddedNetWeight?: number;
}

interface Props {
  item: ItemSummary;
  variation?: string | null;
  priceEmbeddedLineItem?: PriceEmbeddedLineItem;
  showStoreSelectModal?: boolean;
  entryMethod?: EntryMethod;
  orderType?: OrderType | null;
}

const HAPTIC_FEEDBACK_DURATION = 200;


export default /*@__PURE__*/_defineComponent({
  __name: 'WCAddToCartQuantityAdjusterModernCard',
  props: {
    item: {},
    variation: { default: undefined },
    priceEmbeddedLineItem: { default: undefined },
    showStoreSelectModal: { type: Boolean, default: true },
    entryMethod: { default: ENTRY_METHOD.DEFAULT },
    orderType: { default: undefined }
  },
  emits: ['addToCart', 'decrement'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;
const attrs = useAttrs();
const { t } = useI18n();
const store = useStore();

const isInStore = computed(() => store.getters['cart/getInStore']);
const isCustomerModeScan = computed(() => store.getters['user/isCustomerModeScan']);
const setInStore = (value: boolean) => store.dispatch('cart/setInStore', value);

const {
  getMinimumQuantity,
  getQuantityInCart,
  recoverUnroundedQuantityFromCart,
  getDisplayQuantity,
  isNextQuantityIncrementInCartValid,
  alertMaximumQuantityReached,
  increment,
  decrement,
  setQuantity,
  getValidQuantityFromManualEntry,
  isManualEntryAboveMaximum,
  getWeightOptions,
  setWeight,
} = useCartControls(computed(() => props.orderType));

const { isSoldByWeight, isSoldByRandomWeight } = useQuantityDisplay();

const variation = computed(() => props.variation ?? null);
const quantityInCart = computed(() => getQuantityInCart(props.item, variation.value));

const qty = computed(() => quantityInCart.value.toNumber());
const minStep = computed(() => getMinimumQuantity(props.item).toNumber());

/**
 * The quantity to show in the UI, formatted by the composable's getDisplayQuantity
 * (each-count for average weight, rounded weight for random weight, otherwise the number).
 * showAbbr defaults to false, so no unit is appended.
 */
const displayQty = computed(() => getDisplayQuantity(props.item, quantityInCart.value));

const canIncrement = computed(() =>
  isNextQuantityIncrementInCartValid(props.item, variation.value),
);

const weightedQuantityFor = (item: ItemSummary, target: BigNumber): number | null => {
  if (!isSoldByWeight(item)) {
    return null;
  }
  return target.isZero() ? 0 : 1;
};

const isRandomWeight = computed(() => isSoldByRandomWeight(props.item));

const weightOptions = computed(() => getWeightOptions(props.item, variation.value));

const displayWeight = computed(() => getDisplayQuantity(props.item, quantityInCart.value, true));

const incrementButtonRef = ref<HTMLButtonElement | null>(null);

const onClickAddToCart = async () => {
  await setInStore(isCustomerModeScan.value);

  if (isCustomerModeScan.value || (!isInStore.value && !isCustomerModeScan.value)) {
    await increment(
      props.item,
      props.variation,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit('addToCart');
    navigator.vibrate?.(HAPTIC_FEEDBACK_DURATION);

    await nextTick();
    if (!canIncrement.value) {
      alertMaximumQuantityReached();
    }

    if (isInStore.value && !isCustomerModeScan.value) {
      try {
        await ModalService.open(ExitScanAndGo);
      } catch (error) {
        console.error(error);
      }
    }
  }

  await nextTick();
  incrementButtonRef.value?.focus();
};

const onClickDecrement = async () => {
  emit('decrement');
  await decrement(props.item, props.variation);
};

const onSelectWeight = async (value: BigNumber) => {
  const current = quantityInCart.value;
  try {
    await setWeight(
      props.item,
      variation.value,
      value,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit(value.isGreaterThan(current) ? 'addToCart' : 'decrement');
  } catch (error) {
    console.error(error);
  }
};

const onClickRemoveWeight = async () => {
  emit('decrement');
  try {
    await setQuantity(0, 0, props.item, variation.value, props.priceEmbeddedLineItem);
  } catch (error) {
    console.error(error);
  }
};

const isEditing = ref(false);
const inputQty = ref(0);
const qtyInputRef = ref<HTMLInputElement | null>(null);

const autosizeQtyInput = () => {
  const el = qtyInputRef.value;
  if (!el) {
    return;
  }
  el.style.width = '0';
  el.style.width = `${el.scrollWidth + 2}px`;
};

const onQtyClick = async () => {
  isEditing.value = true;
  inputQty.value = Number(displayQty.value);
  await nextTick();
  qtyInputRef.value?.select();
  autosizeQtyInput();
};

const syncKeyboardInset = () => {
  const viewport = window.visualViewport;
  const keyboardOverlap =
    viewport && isEditing.value
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;
  document.documentElement.style.setProperty('--wc-keyboard-overlap', `${keyboardOverlap}px`);
};
onMounted(() => window.visualViewport?.addEventListener('resize', syncKeyboardInset));
onUnmounted(() => window.visualViewport?.removeEventListener('resize', syncKeyboardInset));

const commitQtyInput = async () => {
  isEditing.value = false;
  syncKeyboardInset();
  const raw = inputQty.value;
  const current = recoverUnroundedQuantityFromCart(props.item, variation.value);

  if (typeof raw !== 'number' || Number.isNaN(raw) || raw < 0) {
    inputQty.value = current.toNumber();
    return;
  }

  if (isManualEntryAboveMaximum(raw, props.item)) {
    inputQty.value = current.toNumber();
    alertMaximumQuantityReached();
    return;
  }

  const target = getValidQuantityFromManualEntry(raw, props.item);
  if (target.isEqualTo(current)) {
    inputQty.value = current.toNumber();
    return;
  }

  try {
    await setQuantity(
      target,
      weightedQuantityFor(props.item, target),
      props.item,
      variation.value,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit(target.isGreaterThan(current) ? 'addToCart' : 'decrement');
  } catch (error) {
    console.error(error);
    inputQty.value = current.toNumber();
  }
};

const cancelQtyInput = () => {
  isEditing.value = false;
  syncKeyboardInset();
};

const restrictQtyInput = (e: InputEvent) => {
  const data = e.data;
  if (!data) {
    return;
  }
  if (!/^[0-9.]+$/.test(data)) {
    e.preventDefault();
    return;
  }
  const currentText = (e.target as HTMLInputElement).value;
  const dotCount = (currentText.match(/\./g) || []).length + (data.match(/\./g) || []).length;
  if (dotCount > 1) {
    e.preventDefault();
  }
};

const onQtyKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitQtyInput();
  } else if (e.key === 'Escape' || e.key === 'Esc') {
    e.preventDefault();
    cancelQtyInput();
  } else if (['-', '+', 'e', 'E'].includes(e.key)) {
    e.preventDefault();
  }
};

return (_ctx: any,_cache: any) => {
  return (qty.value === 0)
    ? (_openBlock(), _createElementBlock("button", _mergeProps({
        key: 0,
        id: 'batcbm_addbtn_' + _ctx.item.id,
        type: "button",
        class: "wc-atc-qty-adjuster-modern-card__atc"
      }, _unref(attrs), {
        onClick: _withModifiers(onClickAddToCart, ["stop"])
      }), [
        _createVNode(WCSvgIcon, {
          name: "shopping-cart",
          "icon-size": { widthRem: 1.125, heightRem: 1 }
        }),
        _cache[2] || (_cache[2] = _createTextVNode()),
        _renderSlot(_ctx.$slots, "atc-label", {}, () => [
          _createTextVNode(_toDisplayString(_unref(t)('add')), 1)
        ])
      ], 16, _hoisted_1))
    : (isRandomWeight.value)
      ? (_openBlock(), _createElementBlock("div", _mergeProps({
          key: 1,
          class: "wc-atc-qty-adjuster-modern-card wc-atc-qty-adjuster-modern-card--weight"
        }, _unref(attrs)), [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_unref(t)('quantity')) + " " + _toDisplayString(displayWeight.value), 1),
          _cache[3] || (_cache[3] = _createTextVNode()),
          _createElementVNode("button", {
            type: "button",
            class: "wc-atc-qty-adjuster-modern-card__minus wc-atc-qty-adjuster-modern-card__trash",
            onClick: _withModifiers(onClickRemoveWeight, ["stop"]),
            "aria-label": _unref(t)('decreaseQuantity')
          }, [
            _createVNode(WCSvgIcon, {
              name: "trash",
              "icon-size": { widthRem: 0.875, heightRem: 0.875 }
            })
          ], 8, _hoisted_3),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createVNode(WCMenuDropdownModern, {
            variant: "card",
            "overlay-menu": true,
            options: weightOptions.value,
            label: displayWeight.value,
            "aria-label": _unref(t)('quantity'),
            onSelect: onSelectWeight
          }, null, 8, ["options", "label", "aria-label"])
        ], 16))
      : (_openBlock(), _createElementBlock("div", _mergeProps({
          key: 2,
          class: "wc-atc-qty-adjuster-modern-card"
        }, _unref(attrs)), [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('quantity')) + " " + _toDisplayString(displayQty.value), 1),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["wc-atc-qty-adjuster-modern-card__minus", { 'wc-atc-qty-adjuster-modern-card__trash': qty.value <= minStep.value }]),
            onClick: _withModifiers(onClickDecrement, ["stop"]),
            "aria-label": _unref(t)('decreaseQuantity')
          }, [
            _createVNode(WCSvgIcon, {
              name: qty.value <= minStep.value ? 'trash' : 'minus',
              "icon-size": { widthRem: 0.875, heightRem: 0.875 }
            }, null, 8, ["name"])
          ], 10, _hoisted_5),
          _cache[6] || (_cache[6] = _createTextVNode()),
          (!isEditing.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: "wc-atc-qty-adjuster-modern-card__qty",
                onClick: _withModifiers(onQtyClick, ["stop"]),
                "aria-label": _unref(t)('quantity') + ' ' + displayQty.value
              }, [
                _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)('quantity')), 1),
                _createTextVNode(" " + _toDisplayString(displayQty.value), 1)
              ], 8, _hoisted_6))
            : _withDirectives((_openBlock(), _createElementBlock("input", {
                key: 1,
                ref_key: "qtyInputRef",
                ref: qtyInputRef,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputQty).value = $event)),
                type: "number",
                inputmode: "decimal",
                class: "wc-atc-qty-adjuster-modern-card__qty-input",
                min: minStep.value,
                step: minStep.value,
                "aria-label": _unref(t)('quantity'),
                onBeforeinput: restrictQtyInput,
                onInput: autosizeQtyInput,
                onBlur: commitQtyInput,
                onKeydown: onQtyKeydown,
                onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
              }, null, 40, _hoisted_8)), [
                [
                  _vModelText,
                  inputQty.value,
                  void 0,
                  { number: true }
                ]
              ]),
          _cache[7] || (_cache[7] = _createTextVNode()),
          _createElementVNode("button", {
            ref_key: "incrementButtonRef",
            ref: incrementButtonRef,
            type: "button",
            class: _normalizeClass(["wc-atc-qty-adjuster-modern-card__plus", { 'wc-atc-qty-adjuster-modern-card__plus--disabled': !canIncrement.value }]),
            onClick: _withModifiers(onClickAddToCart, ["stop"]),
            "aria-label": _unref(t)('increaseQuantity')
          }, [
            _createVNode(WCSvgIcon, {
              name: "plus",
              "icon-size": { widthRem: 0.875, heightRem: 0.875 }
            })
          ], 10, _hoisted_9)
        ], 16))
}
}

})